
	import {Component, Inject, Prop, Vue, Watch} from "vue-property-decorator";
	import ExtraServiceService from "@/services/ExtraService";
	import ApiHelper from "@/helpers/api-helper";
	import {contarPedido, ESPERA_DE_PRECIOS, EsperaDePrecios} from "@/helpers/espera-de-precios";
	import PriceHelper from "@/helpers/price-helper";
	import FormatHelper from "@/helpers/format-helper";
	import {opcionalesDelTramo, viajeYaEmpezado} from "@/helpers/tramo-helper";
	import {Cambio, CambiosAlGuardar, Quitado} from "@/helpers/ventana-al-guardar";

	/**
	 * N-287 C1 · Hasta dónde vale una tarifa para un grupo que no cabe: «llega a 60 personas» o «es
	 * desde 20 personas». Sin la tarifa (no se ha visto en ningún catálogo), null.
	 */
	function limiteDe(tarifa: any, personas: number): string | null {
		if (!tarifa) {
			return null;
		}
		if (Number(tarifa.people_max) < personas) {
			return 'llega a ' + tarifa.people_max + ' personas';
		}
		if (Number(tarifa.people_min) > personas) {
			return 'es desde ' + tarifa.people_min + ' personas';
		}
		return null;
	}

	/** N-287 C1 · El número al que hay que volver para que una tarifa vuelva a valer. */
	function vueltaDe(tarifa: any, personas: number): string {
		if (tarifa && Number(tarifa.people_max) < personas) {
			return ' Si vuelves a ' + tarifa.people_max + ' o menos antes de guardar, vuelve.';
		}
		if (tarifa && Number(tarifa.people_min) > personas) {
			return ' Si vuelves a ' + tarifa.people_min + ' o más antes de guardar, vuelve.';
		}
		return '';
	}

	@Component({})
	export default class ExtraServiceSelect extends Vue {
		// N-288 · 4A: GUARDAR espera mientras el catálogo de precios está en camino.
		@Inject({ from: ESPERA_DE_PRECIOS, default: null }) private readonly esperaDePrecios!: EsperaDePrecios | null;
		@Prop({ default: null, required: false }) private model;
		@Prop({ default: null, required: false }) private errors;
		@Prop({ default: null, required: false }) private valid;
		@Prop({ default: null, required: false }) private emit_on_init;
		@Prop({ default: null, required: true }) private travel_id;
		// M-13: días naturales del viaje. Las tarifas marcadas `daily` se cobran por
		// día, así que el total que muestra el panel las multiplica (el motor hace lo
		// mismo con budgets.days). Sin la prop, se comporta como siempre.
		@Prop({ default: 0, required: false }) private days;
		// M-15: pasajeros del viaje (alumnos + profesores). Las tarifas marcadas
		// `group` traen el precio del GRUPO y el motor lo reparte entre ellos; el
		// panel tiene que enseñar el mismo importe. Sin la prop, no se reparte.
		@Prop({ default: 0, required: false }) private people;
		// N-234: cuántas unidades se contrataron de cada servicio extra, tal como las
		// sirve `GET /api/budget/{id}`: una LISTA de parejas
		// `[{extra_service_id, quantity}]`, no un mapa (un mapa con claves numéricas
		// llegaría reindexado por `JsonResource::filter()` y sin los ids).
		// Va aparte del objeto elegido porque ese objeto es la identidad de la casilla.
		// Sin la prop —el formulario de PAQUETE— el selector se comporta como siempre.
		@Prop({ default: null, required: false }) private quantities;
		// N-287 C1: la llegada del viaje. A un viaje que ya empezó no se le aplica el tramo (como en
		// la zona B): el catálogo se pide sin personas, como siempre. El paquete no la manda.
		@Prop({ default: null, required: false }) private start_date;
		// N-287 C2: el mensaje con el que el servidor rechazó el guardado (450 bajo `extra_service`).
		@Prop({ default: null, required: false }) private errores_servidor;

		private items: any[] = [];
		private value: any[] = [];
		private filters: any;
		private isLoaded: boolean;
		/** Unidades por servicio extra, `{extra_service_id: n}`, ya saneadas. */
		private unitsById: any;
		// N-287 C1: lo que pidió el cliente (o marcó el operador a mano), para remapearlo en cada
		// tramo. `null` = aún no se ha leído: se lee al llegar el primer catálogo con personas.
		private pedidos: any[] | null = null;
		// N-287 C1: lo que el último catálogo con personas quitó, y lo que pasó a otra tarifa
		// (`{antes, ahora}`), para decirlo debajo de la lista.
		private quitados: any[] = [];
		private cambiados: any[] = [];
		// N-287 C1: el número con el que se pidió ese catálogo, el que dicen las líneas (con el campo
		// a medio teclear el catálogo se pide sin personas y las líneas siguen hablando de este).
		private personasDelTramo: number | null = null;
		// N-287 C1: las tarifas (`{id: tarifa}`) y los nombres de servicio (`{id: nombre}`) vistos en
		// cualquier catálogo: el motivo de lo quitado («llega a 60») sale de aquí.
		private tarifasVistas: any = {};
		private serviciosVistos: any = {};

		@Watch("model")
		model_change(value) {
			// N-287 C1: un `model` que llega de fuera vuelve a ser lo pedido. El nuestro de vuelta
			// (el padre devuelve por `model` lo que emitimos por `input`) no.
			if (value !== this.value) {
				this.pedidos = null;
			}
			this.value = value;
			this.setPrice();
		}

		@Watch("travel_id")
		travel_id_change(value) {
			this.filters.travel_id = value;
			this.getSelectorList();
		}

		// M-13: los días se editan CON el formulario ya cargado (las fechas del
		// presupuesto, la duración del paquete). Sin vigilarlos, el importe de las
		// tarifas por día se quedaba con la duración vieja mientras el servidor
		// guardaba la nueva. Mismo patrón que TransportSelect, que ya lo hacía.
		@Watch("days")
		days_change(value) {
			this.setPrice();
		}

		// M-15: los pasajeros se editan CON el formulario ya cargado (alumnos y
		// profesores del presupuesto), igual que los días. Mismo motivo, mismo patrón.
		@Watch("people")
		people_change(value) {
			this.setPrice();
			// N-287 C1: y el catálogo del tramo nuevo.
			if (this.ponerPersonas()) {
				this.getSelectorList();
			}
		}

		// N-287 C1: mover la llegada puede convertirlo en un viaje ya empezado, o al revés.
		@Watch("start_date")
		start_date_change() {
			if (this.ponerPersonas()) {
				this.getSelectorList();
			}
		}

		// N-234: las cantidades se editan CON el formulario ya cargado (el presupuesto
		// llega por HTTP después de montar el tab), igual que los días y los pasajeros.
		@Watch("quantities")
		quantities_change(value) {
			this.leerCantidades();
			this.setPrice();
		}

		constructor() {
			super();
			this.isLoaded = false;
			this.filters = { travel_id: null };
			this.unitsById = {};
		}

		/**
		 * 🔴 La identidad de cada casilla, UNA POR TARIFA y SIEMPRE EL MISMO OBJETO.
		 *
		 * La forma es la de toda la vida (M-13); lo que aquí se cuida es la REFERENCIA.
		 * Escrito como literal en la plantilla, el objeto se fabricaba de nuevo en cada
		 * render — y Vuetify no guarda una referencia al `:value`: `VInput` VIGILA esa
		 * prop y la copia en su estado interno. Ese estado interno es el ARRAY del
		 * v-model, así que en cuanto el watcher se disparaba pasaba a ser un objeto,
		 * Vuetify dejaba de tratar la lista como múltiple y pintaba TODAS las casillas
		 * marcadas con una sola elegida. Peor: pulsar una de las falsas destruía la
		 * selección entera, porque `onChange` ya no seguía la rama de lista.
		 *
		 * Estaba latente desde siempre —nada re-renderizaba este componente después de
		 * cargar el catálogo— y lo destapó el campo de cantidad de N-234, que lo
		 * re-renderiza en cada tecla. Medido en la réplica local el 22-sep-2026: un
		 * `$forceUpdate()` pelado bastaba.
		 *
		 * Es un getter y no un método a propósito: un método en la plantilla se
		 * reevalúa en cada render y devolvería otra vez un objeto nuevo. Depende solo
		 * de `items`, que se asigna entero (nunca se muta por dentro).
		 */
		get opcionesPorTarifa() {
			const opciones = {};
			for (const item of this.items) {
				for (const price of item.prices) {
					opciones[price.id] = {
						extra_service_id: item.id,
						prices: { id: price.id, value: price.price },
						extra_service_type_name: price.extra_service_type_name,
					};
				}
			}
			return opciones;
		}

		mounted() {
			this.leerCantidades();
			if (this.model) {
				this.value = this.model;
			}
			if (this.travel_id) {
				this.filters.travel_id = this.travel_id;
			}
			this.ponerPersonas();
			if (this.filters.travel_id) {
				this.getSelectorList();
			}
		}

		/**
		 * N-287 C1 · El catálogo se pide con los pasajeros: el servidor solo da las tarifas que valen
		 * para ese número, y deja fuera el servicio entero si no vale ninguna. Sin un número válido
		 * (el campo a medio teclear), en el editor de paquetes sin pasajeros o en un viaje ya
		 * empezado, se pide sin ellos, como siempre. Devuelve si los filtros han cambiado.
		 */
		ponerPersonas() {
			const personas = Number(this.people);
			const conPersonas = Number.isInteger(personas) && personas > 0 && !viajeYaEmpezado(this.start_date);
			const nuevo = conPersonas ? personas : null;
			const cambia = this.filters.people !== nuevo;
			this.filters.people = nuevo;
			return cambia;
		}

		getSelectorList() {
			// N-287 C1: al teclear «72» se pide también el catálogo de «7»; si esa respuesta llega la
			// última, su tramo no debe pisar al bueno.
			const pedidoCon = JSON.stringify(this.filters);
			const conPersonas = this.filters.people !== null && this.filters.people !== undefined;
			return contarPedido(this.esperaDePrecios, ExtraServiceService.getSelectorList(null, this.filters)
				.then((res) => {
					if (pedidoCon !== JSON.stringify(this.filters)) {
						return;
					}
					if (res.status === 200) {
						this.items = res.data.elements;
						// N-287 (revisión, C-1): un catálogo nuevo da a cada casilla un `:value` nuevo, y
						// Vuetify lo copia en su estado interno, que es el ARRAY del v-model: sin un array
						// nuevo en el mismo render, la casilla deja de ser de lista y se pinta MARCADA (la
						// trampa de la rule del panel). `renovarTramo` ya asigna uno; aquí, para el resto.
						this.value = (this.value || []).slice();
						this.recordarTarifas(this.items);
						this.isLoaded = true;
						if (conPersonas) {
							this.renovarTramo();
						} else if (this.emit_on_init && this.model) {
							this.setPrice();
						} else if (this.value && this.value.length) {
							// M-13: la UNIDAD de cada tarifa vive en el catálogo, que llega
							// DESPUÉS que las opciones ya elegidas: hasta aquí el importe se
							// calculó como si todas fueran fijas. Se re-emite ya con las
							// unidades aplicadas. Solo el importe: depurar las opciones
							// marcadas sigue siendo cosa de setPrice (un cambio del usuario),
							// no de abrir la ficha.
							this.$emit('setPrice', this.totalAmount());
							this.$emit('setSeats', this.teacherSeats());
						}
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				}));
		}

		/**
		 * N-287 C1 · Las tarifas y los nombres de servicio de un catálogo, para poder decir después
		 * por qué se quitó algo. Se guardan TODOS los catálogos (también el de otro número).
		 */
		recordarTarifas(elementos) {
			const tarifas = Object.assign({}, this.tarifasVistas);
			const servicios = Object.assign({}, this.serviciosVistos);
			for (const servicio of elementos || []) {
				servicios[servicio.id] = servicio.name;
				for (const tarifa of servicio.prices || []) {
					tarifas[tarifa.id] = tarifa;
				}
			}
			this.tarifasVistas = tarifas;
			this.serviciosVistos = servicios;
		}

		/**
		 * N-287 C1 (decisión de Bernardo, 8-oct-2026) · Llega el catálogo del tramo nuevo. Cada
		 * servicio PEDIDO sigue si su tarifa está; si no, pasa a la del MISMO NOMBRE del mismo
		 * servicio (la regla de N-286 y de la zona A); si no hay, se quita, y se dice debajo de la
		 * lista. Se parte siempre de lo pedido, así que un número intermedio no lo hace olvidar.
		 */
		renovarTramo() {
			if (this.pedidos === null) {
				this.pedidos = (this.value || []).slice();
			}
			const elegidos: any[] = [];
			const quitados: any[] = [];
			const cambiados: any[] = [];
			for (const pedido of this.pedidos) {
				const servicio = this.items.find((item) => item.id == pedido.extra_service_id);
				const delServicio = servicio
					? servicio.prices.map((tarifa) => ({ id: tarifa.id, name: tarifa.extra_service_type_name }))
					: [];
				const tarifa = opcionalesDelTramo(
					[{ id: pedido.prices.id, name: pedido.extra_service_type_name }], delServicio
				)[0];
				if (!tarifa) {
					quitados.push(pedido);
					continue;
				}
				// Dos tarifas pedidas pueden acabar en la misma (las dos de las Papeletas de Valencia): una vez.
				let destino = elegidos.find((ya) => ya.prices.id == tarifa.id);
				if (!destino) {
					destino = tarifa.id == pedido.prices.id ? pedido : this.opcionesPorTarifa[tarifa.id];
					elegidos.push(destino);
				}
				if (tarifa.id != pedido.prices.id) {
					cambiados.push({ antes: pedido, ahora: destino });
				}
			}
			this.value = elegidos;
			this.quitados = quitados;
			this.cambiados = cambiados;
			this.personasDelTramo = this.filters.people;
			this.$emit('setQuitados', quitados.map((pedido) => ({
				extra_service_id: pedido.extra_service_id,
				nombre: this.nombreDelServicio(pedido),
			})));
			// Al abrir un presupuesto que YA estaba fuera, su tarifa no sale en ningún catálogo pedido
			// con personas: una sola petición sin ellas dice hasta dónde llegaba.
			if (quitados.some((pedido) => !this.tarifasVistas[pedido.prices.id])) {
				this.aprenderTarifas();
			}
			this.setPrice();
		}

		/** N-287 C1 · El catálogo del viaje sin personas, solo para saber los topes de lo quitado. */
		aprenderTarifas() {
			return ExtraServiceService.getSelectorList(null, { travel_id: this.filters.travel_id })
				.then((res) => {
					if (res.status === 200) {
						this.recordarTarifas(res.data.elements);
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				});
		}

		/**
		 * N-287 C1 · Lo que el operador marca o desmarca a mano pasa a ser lo pedido. Lo que el tramo
		 * quitó o cambió de tarifa no está en la lista y no se ha podido desmarcar: se sigue pidiendo
		 * (revisión, C-3), salvo que desmarque la tarifa a la que pasó.
		 */
		alCambiarAMano() {
			const marcados = this.value || [];
			this.cambiados = this.cambiados.filter((cambio) => marcados.includes(cambio.ahora));
			this.pedidos = marcados.map((elegido) => {
				const cambio = this.cambiados.find((c) => c.ahora === elegido);
				return cambio ? cambio.antes : elegido;
			}).concat(this.quitados);
			this.setPrice();
		}

		nombreDelServicio(elegido) {
			return this.serviciosVistos[elegido.extra_service_id] || elegido.extra_service_type_name;
		}

		/**
		 * N-287 C1 · Las líneas que explican lo que el último cambio de pasajeros quitó o cambió de
		 * tarifa. Desaparecen cuando el número vuelve a valer.
		 */
		get lineasDelTramo() {
			const personas = Number(this.personasDelTramo);
			const lineas: string[] = [];
			for (const cambio of this.cambiados) {
				const limite = limiteDe(this.tarifasVistas[cambio.antes.prices.id], personas);
				lineas.push('«' + this.nombreDelServicio(cambio.antes) + '» pasa de '
					+ FormatHelper.euros(cambio.antes.prices.value) + ' € a ' + FormatHelper.euros(cambio.ahora.prices.value) + ' €'
					+ (limite ? ': la tarifa de antes ' + limite + ' y el grupo es de ' + personas : '') + '.');
			}
			for (const pedido of this.quitados) {
				const tarifa = this.tarifasVistas[pedido.prices.id];
				const limite = limiteDe(tarifa, personas);
				const unidades = this.unitsFor(pedido);
				lineas.push('Se ha quitado «' + this.nombreDelServicio(pedido) + '» (' + pedido.extra_service_type_name + ' '
					+ FormatHelper.euros(pedido.prices.value) + ' €' + (unidades > 1 ? ', ' + unidades + ' unidades' : '') + '): '
					+ (limite ? 'su tarifa ' + limite + ' y el grupo es de ' + personas : 'su tarifa no vale para un grupo de ' + personas)
					+ '.' + vueltaDe(tarifa, personas));
			}
			return lineas;
		}

		/**
		 * N-288 · T3 · Para la ventana de GUARDAR: lo que el tramo nuevo quitó (con hasta dónde llegaba su
		 * tarifa) y lo que pasó a una tarifa de otro precio. Lo mismo que dicen las líneas de debajo.
		 */
		cambiosAlGuardar(): CambiosAlGuardar {
			const personas = Number(this.personasDelTramo);
			const quitan = this.quitados.map((pedido): Quitado => ({
				texto: 'Servicios extra · ' + this.nombreDelServicio(pedido),
				// «llega a 55», como las actividades y el hotel en la misma lista.
				motivo: (limiteDe(this.tarifasVistas[pedido.prices.id], personas) || '').replace(/ personas$/, ''),
				causa: 'personas',
			}));
			const cambian = this.cambiados
				.filter((cambio) => Number(cambio.antes.prices.value) !== Number(cambio.ahora.prices.value))
				.map((cambio): Cambio => ({
					texto: 'Servicios extra · ' + this.nombreDelServicio(cambio.antes),
					antes: cambio.antes.prices.value,
					ahora: cambio.ahora.prices.value,
					unidad: (this.isGroup(cambio.ahora) ? 'el grupo' : 'por persona') + (this.isDaily(cambio.ahora) ? ' y día' : ''),
				}));
			return { quitan, cambian };
		}

		/**
		 * N-287 C3 (decisión de Bernardo, 8-oct-2026) · Un servicio despublicado solo sale si está
		 * elegido (los 24 presupuestos con un seguro viejo lo conservan); si se desmarca, desaparece.
		 * Con un servidor que no dice `status_id`, salen todos, como antes.
		 */
		get itemsVisibles() {
			return this.items.filter((item) => !this.esDespublicado(item)
				|| (this.value || []).some((elegido) => elegido.extra_service_id == item.id));
		}

		esDespublicado(item) {
			return item.status_id !== undefined && item.status_id !== null && Number(item.status_id) === 0;
		}

		setPrice(){
			// Si se han cargado las opciones, se quitan las marcadas que no están en el catálogo (el
			// filtro de personas). N-287: con `filter` y no con `splice` dentro de un `for…in`, que
			// se saltaba la siguiente a cada una que quitaba.
			if (this.isLoaded) {
				const enCatalogo = (elegido) => !!this.catalogPriceFor(elegido);
				if (!(this.value || []).every(enCatalogo)) {
					this.value = this.value.filter(enCatalogo);
				}
			}
			this.$emit('input', this.value);
			this.$emit('setPrice', this.totalAmount());
			// N-235: y cuántas plazas de profesor ocupan, que el formulario suma a los
			// profesores de pago para enseñar el mismo total que el motor.
			this.$emit('setSeats', this.teacherSeats());
		}

		/**
		 * N-235 · Plazas de profesor de pago que ocupan las opciones marcadas: una por
		 * unidad de las tarifas con `teacher_seat`. Espejo de
		 * `BudgetExtraService::teacherSeats()`. El flag sale del CATÁLOGO, como `daily` y
		 * `group`: el objeto elegido es la identidad de la casilla (M-13).
		 */
		teacherSeats() {
			let seats = 0;
			for (const extraService of this.value || []) {
				const price = this.catalogPriceFor(extraService);
				if (price && Number(price.teacher_seat) === 1) {
					seats += this.unitsFor(extraService);
				}
			}
			return seats;
		}

		/** Suma de las opciones elegidas, cada una con SU unidad. */
		totalAmount() {
			let total = 0;
			for (const extraService of this.value) {
				total += this.amountFor(extraService);
			}
			return total;
		}

		// M-13 + M-15: importe de una tarifa elegida aplicando sus unidades, en el mismo
		// orden que el motor (`BudgetExtraServicePrice::amountFor`): × días y después
		// ÷ pasajeros. El presupuesto guarda (y devuelve) el precio de BD sin tocar —es
		// la identidad de la casilla—; las unidades vienen del catálogo (`items`), que
		// es quien trae los flags `daily` y `group`.
		// N-234 · Las UNIDADES contratadas se multiplican aquí, antes que nada, para que
		// el redondeo del reparto de grupo caiga una sola vez sobre la línea entera: dos
		// coordinadores de 160 €/día en 5 días con 27 pasajeros son 59 €/alumno, no
		// 2 × 30 = 60. Si el panel redondeara por unidad, enseñaría un euro más que el
		// PDF y que el `total_amount` guardado.
		//
		// Se aplican a CUALQUIER tarifa, también a las que no admiten varias: la cantidad
		// es de la LÍNEA (`budgets_extra_services.quantity`) y no de la tarifa, y el motor
		// hace exactamente eso (`BudgetExtraService::amountFor` se las pasa a todas sus
		// tarifas). Gatearlas aquí por `multiple` descuadraría el panel contra el servidor
		// en cuanto alguien cambiara de tarifa dejando una cantidad puesta.
		amountFor(extraService) {
			const price = this.catalogPriceFor(extraService);
			let amount = (Number(extraService.prices.value) || 0) * this.unitsFor(extraService);
			if (this.isDaily(extraService)) {
				amount = amount * this.tripDays();
			}
			// M-16: el reparto lo hace la fuente única, no una rama escrita aquí. El
			// flag sale del CATÁLOGO y nunca del objeto elegido: ese objeto es la
			// identidad de la casilla (igualdad profunda de Vuetify, M-13).
			return PriceHelper.amountPerPassenger(amount, price && price.group, this.people);
		}

		isDaily(extraService) {
			const price = this.catalogPriceFor(extraService);
			return !!price && Number(price.daily) === 1;
		}

		isGroup(extraService) {
			const price = this.catalogPriceFor(extraService);
			return !!price && Number(price.group) === 1;
		}

		/**
		 * Espejo exacto de `BudgetExtraService::units()` en el backend: sin cantidad
		 * declarada —o con una imposible: 0, un negativo, texto— se cobra UNA, que es el
		 * comportamiento de toda la vida y el de las 30.657 líneas anteriores a N-234.
		 * Aquí no se decide producto: se impide que un dato imposible RESTE del total.
		 */
		unidadesSaneadas(quantity) {
			const units = Math.floor(Number(quantity) || 0);
			return units > 0 ? units : 1;
		}

		/**
		 * ¿Se pinta el campo de cantidad de esta tarifa del catálogo? Solo al editar un
		 * PRESUPUESTO (un paquete no guarda unidades y no recibe la prop) y solo si la
		 * tarifa admite varias. Una lista VACÍA sigue siendo un presupuesto: lo que
		 * distingue los dos formularios es que la prop llegue, no que traiga algo.
		 *
		 * Recibe la fila del CATÁLOGO (la del `v-for`), como `daily` y `group`: el flag
		 * no puede salir del objeto elegido, que es la identidad de la casilla (M-13).
		 */
		mostrarCantidad(price) {
			return !!this.quantities && Number(price && price.multiple) === 1;
		}

		/** Unidades contratadas de un servicio extra, por su id. */
		unidadesDe(extraServiceId) {
			return this.unidadesSaneadas(this.unitsById[extraServiceId]);
		}

		/** Unidades de la línea a la que pertenece una tarifa elegida. */
		unitsFor(extraService) {
			return this.unidadesDe(extraService && extraService.extra_service_id);
		}

		/** Vuelca la lista de parejas que sirve el presupuesto en el mapa interno. */
		leerCantidades() {
			const mapa = {};
			for (const pareja of this.quantities || []) {
				mapa[pareja.extra_service_id] = this.unidadesSaneadas(pareja.quantity);
			}
			this.unitsById = mapa;
		}

		/** Las cantidades, otra vez como lista de parejas, para devolverlas al formulario. */
		cantidades() {
			const lista: any[] = [];
			for (const extraServiceId of Object.keys(this.unitsById)) {
				lista.push({
					extra_service_id: Number(extraServiceId),
					quantity: this.unitsById[extraServiceId],
				});
			}
			return lista;
		}

		/**
		 * El operador cambia la cantidad de un servicio. El suelo es UNO (decisión de
		 * Bernardo, 22-sep-2026): quitar el servicio es cosa de la casilla, no del número,
		 * y el servidor rechaza con 422 lo que no sea un entero ≥ 1. El campo numérico
		 * emite TEXTO, de ahí el saneado.
		 */
		setUnits(extraServiceId, quantity) {
			this.$set(this.unitsById, extraServiceId, this.unidadesSaneadas(quantity));
			this.$emit('setQuantity', this.cantidades());
			this.setPrice();
		}

		/** La fila del catálogo que corresponde a una opción elegida (o null). */
		catalogPriceFor(extraService) {
			for (const item of this.items) {
				if (item.id != extraService.extra_service_id) {
					continue;
				}
				for (const price of item.prices) {
					if (price.id == extraService.prices.id) {
						return price;
					}
				}
			}
			return null;
		}

		tripDays() {
			const days = Number(this.days) || 0;
			return days > 0 ? days : 1;
		}
	}
